import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The person menu's plain "Help" item (plan-019 WAPP-HOST; OD-24; design §31.8.9, §31.9.1).
 *
 * The visible fallback for when a content blocker hides the launcher or refuses the help socket: while help is on
 * (`GET /v1/public/help/status`), the person menu offers "Help", which opens the panel when the widget is mounted and
 * reachable, or the plain `/help` page — the feedback and support form in the portal's own bundle — when it is not.
 * Nothing while help is off, and nothing while an operator acts for the person (help is refused to them).
 *
 * The state comes from `HelpRoot` through {@link HelpEntryContext}, so the shell passes nothing and `shell.tsx` is not
 * edited (ED-26).
 */
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import { createContext, use, type ReactNode } from "react";

import { GLYPH } from "../shell/glyph.js";

/** What the Help item does now. */
export type HelpEntry =
  | { readonly kind: "none" }
  | { readonly kind: "panel"; readonly open: () => void }
  | { readonly kind: "page" };

export const HELP_ENTRY_NONE: HelpEntry = Object.freeze({ kind: "none" });

export const HelpEntryContext = createContext<HelpEntry>(HELP_ENTRY_NONE);

/** The person menu's "Help" row; `onChosen` closes the menu. */
export function HelpMenuItem({ onChosen }: { readonly onChosen: () => void }): ReactNode {
  usePortalTranslation("help");
  const entry = use(HelpEntryContext);
  if (entry.kind === "none") return null;
  const label = (
    <>
      <Icon glyph={GLYPH.question} size="lg" />
      <span className="c-pop-item-body">{portalText("help", "entry.menu_item")}</span>
    </>
  );
  if (entry.kind === "panel") {
    return (
      <button
        type="button"
        role="menuitem"
        className="c-pop-item"
        onClick={() => {
          onChosen();
          entry.open();
        }}
      >
        {label}
      </button>
    );
  }
  return (
    <Link to="/help" role="menuitem" className="c-pop-item" onClick={onChosen}>
      {label}
    </Link>
  );
}
